<template>
  <div class="animated fadeIn">
    <instrument-runner-badge-stack :instance="instance" />

    <instrument-runner-sample-filter
      :samples_loading="tasks_loading"
      :collection="task_collection"
      :is_grinder="isGrinder"
      @updateSampleFilters="updateTaskFilters"
    />

    <template v-if="export_data.schedulable_process_id">
      <b-row class="pr-3 mb-2">
        <b-col class="px-0 text-right">
          <InstrumentRunnerExportButton
            :export_data="export_data"
            @newResultExportCreated="refetchExport"
          />
        </b-col>
      </b-row>

      <result-export-table
        v-if="!exports_loading"
        :collection="export_collection"
        @dataRefreshRequest="refetchExport"
      />
    </template>

    <template v-if="isGrinder && hasExportData">
      <b-button @click="updateShowGrindOrder()" variant="primary" size="md">{{buttonLabel}}</b-button>
      <span v-if="showGrindOrder">
        <GrindOrderLoader 
          :sample_id="export_data.sample_type_id"
          :schedule_task_type="export_data.schedulable_process_type"
        />
      </span>
    </template>

  </div>
</template>

<script>
import moment from "moment";
import BadgeBatch from "@/components/shared/BadgeBatch";
import InstrumentRunnerSampleTable from "@/components/lab/instrumentrunner/InstrumentRunnerSampleTable";
import InstrumentRunnerExportButton from "@/components/lab/instrumentrunner/InstrumentRunnerExportButton";
import InstrumentRunnerBadgeStack from "@/components/lab/instrumentrunner/InstrumentRunnerBadgeStack";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import ResultExportTable from "@/components/lab/resultexport/ResultExportTable";
import InstrumentRunnerSampleFilter from "./InstrumentRunnerSampleFilter";
import LoadingOverlay from "@/components/shared/LoadingOverlay";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";
import { getCollection } from "@/shared/fetch-utils";
import GrindOrderLoader from "./grindorder/GrindOrderLoader.vue";

const InstrumentInstanceRepository =
  LabRepositoryFactory.get("instrumentInstance");

export default {
  name: "InstrumentRunnerInstanceTab",
  components: {
    BadgeBatch,
    InstrumentRunnerSampleTable,
    InstrumentRunnerExportButton,
    InstrumentRunnerSampleFilter,
    InstrumentRunnerBadgeStack,
    ResultExportTable,
    LoadingOverlay,
    GrindOrderLoader
},
  mixins: [ToastNotifications],
  props: {
    instance: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      export_data: {
        laboratory_id: this.$store.getters.getLaboratoryContext.id,
        instrument_instance_id: this.instance.id,
        sample_type_id: null,
        schedulable_process_id: null,
        schedulable_process_type: null,
        tasks: null,
      },
      task_collection: null,
      tasks_loading: true,
      export_collection: null,
      exports_loading: true,
      showGrindOrder: false,
    };
  },
  computed: {
    buttonLabel() {
      return this.showGrindOrder ? 'Hide Grind Order' : 'Show Grind Order';
    },
    isGrinder() {
      return this.instance.identifier.includes('Grinder');
    },
    hasExportData() {
      return this.export_data.sample_type_id && 
            (this.export_data.schedulable_process_type || this.export_data.schedulable_process_id)
    },
  },
  methods: {
    updateShowGrindOrder() {
      this.showGrindOrder = !this.showGrindOrder;
    },
    /**
     * update filters
     */
    updateTaskFilters(value) {
      // spread values into params
      const { process_type, sample_type, process, tasks } = { ...value };

      // set update exports
      let update_exports = false;

      // check if field needs to be updated
      if (this.export_data["schedulable_process_type"] !== process_type) {
        this.export_data["schedulable_process_type"] = process_type
          ? process_type
          : null;
        update_exports = true;
      }

      // check if field needs to be updated
      if (this.export_data["schedulable_process_id"] !== process) {
        this.export_data["schedulable_process_id"] = process ? process : null;
        update_exports = true;
      }

      // check if field needs to be updated
      if (this.export_data["sample_type_id"] !== sample_type) {
        this.export_data["sample_type_id"] = sample_type ? sample_type : null;
        update_exports = true;
      }

      // check if field needs to be updated
      if (this.export_data["tasks"] !== tasks) {
        this.export_data["tasks"] = tasks ? tasks : null;
      }

      // check if we needs to re-fetch exports due to filter changes
      if (update_exports) {
        this.fetchExports();
      }
    },

    refetchExport() {
      this.fetchExports();
    },

    /**
     * Fetch task information based on the selected instrument instance
     */
    async fetchTasks() {
      this.tasks_loading = true;
      const { data } = await InstrumentInstanceRepository.getTasks({
        id: this.instance.id,
        limit: "all",
        date: moment().format("YYYY-MM-DD"),
      });

      this.task_collection = data;
      this.tasks_loading = false;
    },

    /**
     * Fetch exports
     */
    async fetchExports() {
      // if enough data is set: fetch exports
      if (
        this.export_data.sample_type_id &&
        this.export_data.schedulable_process_type &&
        this.export_data.schedulable_process_id
      ) {
        this.export_collection = await getCollection({
          collection_type: "result_export",
          filters: [
            {
              logic: "filterEquals",
              field: "sample_type_id",
              value: this.export_data.sample_type_id,
            },
            {
              logic: "filterEquals",
              field: "schedulable_process_type",
              value:
                "App\\Models\\" +
                (this.export_data.schedulable_process_type === "prep"
                  ? "Prep"
                  : "Test") +
                "Process",
            },
            {
              logic: "filterEquals",
              field: "schedulable_process_id",
              value: this.export_data.schedulable_process_id,
            },
            {
              logic: "filterEquals",
              field: "date",
              value: moment().format("YYYY-MM-DD"),
            },
          ],
          sort: [
            {
              logic: "sortAddDesc",
              field: "created_at",
            },
          ],
        });

        // set loading flag
        this.exports_loading = false;
      } else {
        // reset collection to null - there is not enough info
        this.export_collection = null;
      }
    },
    /**
     * Init data in the obj
     */
    async init() {
      await this.fetchTasks();
    },
  },
  created() {
    this.init();
  },
};
</script>
